import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { COMPANY } from "../data/site";
import { InquiryForm } from "../components/InquiryForm";
import { Reveal } from "../components/Reveal";
import { Mail, Phone, MapPin } from "lucide-react";
import { WhatsAppIcon } from "../components/WhatsAppIcon";
import { usePageTitle } from "../lib/usePageTitle";

export default function Contact() {
    usePageTitle("meta.contact");
    const { t } = useTranslation();
    const [params] = useSearchParams();
    const defaultProduct = params.get("product") || undefined;

    return (
        <main data-testid="page-contact" className="pt-32">
            <section className="px-6 md:px-12 lg:px-20 pt-12 pb-16">
                <Reveal>
                    <p className="text-[10px] uppercase tracking-[0.35em] text-[#C5A059]">
                        {t("contact.overline")}
                    </p>
                    <h1 className="mt-6 font-heading text-5xl md:text-6xl lg:text-7xl text-[#E8E3D9] leading-[1.02] tracking-tighter max-w-5xl">
                        {t("contact.h1_a")}{" "}
                        <span className="italic text-[#C5A059]">
                            {t("contact.h1_b")}
                        </span>
                    </h1>
                    <p className="mt-8 max-w-2xl text-[#A39E93] text-base md:text-lg font-light leading-relaxed">
                        {t("contact.intro")}
                    </p>
                </Reveal>
            </section>

            <section className="px-6 md:px-12 lg:px-20 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                <Reveal className="lg:col-span-7">
                    <InquiryForm defaultProduct={defaultProduct} />
                </Reveal>

                <Reveal className="lg:col-span-5" delay={120}>
                    <div className="bg-[#1A1A1A] border border-[#2C2C2C] p-8 md:p-10">
                        <p className="text-[10px] uppercase tracking-[0.35em] text-[#C5A059]">
                            {t("contact.direct")}
                        </p>
                        <ul className="mt-8 space-y-6">
                            <li className="flex gap-4">
                                <MapPin
                                    size={18}
                                    className="text-[#C5A059] mt-1 shrink-0"
                                    strokeWidth={1.5}
                                />
                                <div>
                                    <p className="text-[10px] uppercase tracking-[0.3em] text-[#A39E93]">
                                        {t("contact.address")}
                                    </p>
                                    <p
                                        data-testid="contact-address"
                                        className="mt-2 text-[#E8E3D9] font-light leading-relaxed"
                                    >
                                        {COMPANY.legalName}
                                        <br />
                                        {COMPANY.address.line1}
                                        <br />
                                        {COMPANY.address.line2}
                                        <br />
                                        {COMPANY.address.district},{" "}
                                        {COMPANY.address.state}{" "}
                                        {COMPANY.address.postal}
                                        <br />
                                        {COMPANY.address.country}
                                    </p>
                                </div>
                            </li>
                            <li className="flex gap-4">
                                <Phone
                                    size={18}
                                    className="text-[#C5A059] mt-1 shrink-0"
                                    strokeWidth={1.5}
                                />
                                <div>
                                    <p className="text-[10px] uppercase tracking-[0.3em] text-[#A39E93]">
                                        {t("contact.phone")}
                                    </p>
                                    <a
                                        href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
                                        data-testid="contact-phone-link"
                                        className="mt-2 text-[#E8E3D9] hover:text-[#C5A059] transition-colors block"
                                    >
                                        {COMPANY.phone}
                                    </a>
                                </div>
                            </li>
                            <li className="flex gap-4">
                                <Mail
                                    size={18}
                                    className="text-[#C5A059] mt-1 shrink-0"
                                    strokeWidth={1.5}
                                />
                                <div>
                                    <p className="text-[10px] uppercase tracking-[0.3em] text-[#A39E93]">
                                        {t("contact.email")}
                                    </p>
                                    <a
                                        href={`mailto:${COMPANY.email}`}
                                        data-testid="contact-email-link"
                                        className="mt-2 text-[#E8E3D9] hover:text-[#C5A059] transition-colors block"
                                    >
                                        {COMPANY.email}
                                    </a>
                                </div>
                            </li>
                            <li className="flex gap-4">
                                <WhatsAppIcon
                                    size={18}
                                    className="text-[#C5A059] mt-1 shrink-0"
                                />
                                <div>
                                    <p className="text-[10px] uppercase tracking-[0.3em] text-[#A39E93]">
                                        {t("contact.whatsapp")}
                                    </p>
                                    <a
                                        href={`https://wa.me/${COMPANY.whatsapp}`}
                                        data-testid="contact-whatsapp-link"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="mt-2 text-[#E8E3D9] hover:text-[#C5A059] transition-colors block"
                                    >
                                        {t("contact.whatsapp_cta")}
                                    </a>
                                </div>
                            </li>
                        </ul>
                    </div>
                </Reveal>
            </section>

            {/* MAP */}
            <section className="border-t border-[#2C2C2C]">
                <div className="px-6 md:px-12 lg:px-20 py-16">
                    <p className="text-[10px] uppercase tracking-[0.35em] text-[#C5A059]">
                        {t("contact.map_overline")}
                    </p>
                    <h3 className="mt-3 font-heading text-3xl md:text-4xl text-[#E8E3D9] tracking-tight">
                        {t("contact.map_title")}
                    </h3>
                </div>
                <div data-testid="contact-map" className="w-full h-[60vh]">
                    <iframe
                        title="Jagruthi Stones LLP"
                        src={COMPANY.mapEmbed}
                        className="w-full h-full border-0 filter grayscale contrast-125"
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        allowFullScreen
                    />
                </div>
            </section>
        </main>
    );
}
